import { Form, Button, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useIntl } from 'react-intl';
import { useState } from 'react';
import Config from '../../config/Config';
import '../../css/DppForm.css';

// Validation schema
const schema = z.object({
  Priority: z.enum(['Low', 'Medium', 'High', 'Very high'], {
    required_error: 'Priority is required',
  }),
  ShortText: z.enum(['Display Bug', 'Other support'], {
    required_error: 'Short description is required',
  }),
  ServiceType: z.enum([
    'Inner Inspection',
    'Maintenance',
    'Preventive Maintenance',
    'Repair',
    'Overhauling',
    'Software/Firmware and Parameter Update',
    'SpareParts',
    'WearParts',
    'Retrofit measures (also conversion or modernization)',
    'Other support'
  ], {
    required_error: 'Service type is required',
  }),
  LongText: z.string().min(10, { message: 'Description must be at least 10 characters' }).optional(),
  StartOfFault: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Format: YYYY-MM-DD'),
  ErrorCode: z.string().optional(),

  AttachedMedia: z.any()
  .optional()
  .refine((files) => !files || files.length <= 5, {
    message: 'Maximum 5 files allowed',
  })
  .refine(
    (files) =>
      !files ||
      files.length === 0 ||
      Array.from(files).every((file) => file.size <= 20 * 1024 * 1024),
    {
      message: 'Each file must be smaller than 20 MB',
    }
  )
  .refine(
    (files) =>
      !files ||
      files.length === 0 ||
      Array.from(files).reduce((sum, file) => sum + file.size, 0) <=
        20 * 1024 * 1024,
    {
      message: 'Total size of all attached files cannot exceed 20 MB',
    }
  )
  .refine(
    (files) =>
      !files ||
      files.length === 0 ||
      Array.from(files).every((file) =>
        ['image/jpeg', 'image/png', 'image/gif'].includes(file.type)
      ),
    {
      message: 'Only JPG, PNG and GIF files are allowed',
    }
  ),

  CustomerNumber: z.string().optional(),
  Company: z.string().optional(),
  FirstName: z.string().min(1, { message: 'First name is required' }),
  LastName: z.string().min(1, { message: 'Last name is required' }),
  Email: z.email({ message: 'Invalid email address' }),

  Phone: z.union([
    z.string().regex(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number'),
    z.literal(''),
    z.undefined(),
  ]).optional(),

  CityTown: z.string().optional(),
  Street: z.string().optional(),
  StateCounty: z.string().optional(),
  NationalCode: z.string().max(2, { message: 'Country must be written as 2-letter national code' }).optional(),
  Zipcode: z.string().optional(),
});

const ReportProblemForm = ({
  showButtons = true,
  onSave,
  onCancel,
  isModal = false,
}) => {
  const intl = useIntl();
  const isDemo = Config.isDemo === 'true';
  const noAccessMessage = intl.formatMessage({ id: 'no_access_message', defaultMessage: 'Not authorized for this action' });
  const [startOfFault, setStartOfFault] = useState(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
    setValue,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      Priority: 'Medium',
      ShortText: 'Other support',
      ServiceType: 'Repair',
      LongText: '',
      StartOfFault: new Date().toISOString().split('T')[0],
      ErrorCode: '',
      AttachedMedia: null,
      CustomerNumber: '',
      Company: '',
      FirstName: '',
      LastName: '',
      Email: '',
      Phone: '',
      CityTown: '',
      Street: '',
      StateCounty: '',
      NationalCode: '',
      Zipcode: '',
    },
    mode: 'onTouched',
  });

  const handleCancel = () => {
    reset();
    setStartOfFault(null);
    onCancel?.();
  };

  const handleSave = (data) => {
    // Transform data for backend
    const cleanedData = {
      ...data,
      AttachedMedia: data.AttachedMedia ? Array.from(data.AttachedMedia) : undefined,
    };
    onSave?.(cleanedData);
    if (isModal) {
      reset();
      setStartOfFault(null);
    }
  };

  return (
    <Form onSubmit={handleSubmit(handleSave)} noValidate className="dpp-form">
      {/* Problem Details */}
      <Row>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="Priority">
            <Form.Label>{intl.formatMessage({ id: 'priority' })}</Form.Label>
            <Form.Select
              {...register('Priority')}
              isInvalid={!!errors.Priority}
              className="dpp-form-control"
            >
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Very high">Very high</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.Priority?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="ShortText">
            <Form.Label>{intl.formatMessage({ id: 'short_text' })}</Form.Label>
            <Form.Select
              {...register('ShortText')}
              isInvalid={!!errors.ShortText}
              className="dpp-form-control"
            >
              <option value="Display Bug">Display Bug</option>
              <option value="Other support">Other support</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.ShortText?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="ServiceType">
            <Form.Label>{intl.formatMessage({ id: 'service_type' })}</Form.Label>
            <Form.Select
              {...register('ServiceType')}
              isInvalid={!!errors.ServiceType}
              className="dpp-form-control"
            >
              <option value="Inner Inspection">Inner Inspection</option>
              <option value="Maintenance">Maintenance</option>
              <option value="Preventive Maintenance">Preventive Maintenance</option>
              <option value="Repair">Repair</option>
              <option value="Overhauling">Overhauling</option>
              <option value="Software/Firmware and Parameter Update">Software/Firmware and Parameter Update</option>
              <option value="SpareParts">Spare Parts</option>
              <option value="WearParts">Wear Parts</option>
              <option value="Retrofit measures (also conversion or modernization)">Retrofit measures (also conversion or modernization)</option>
              <option value="Other support">Other support</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.ServiceType?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="ErrorCode">
            <Form.Label>{intl.formatMessage({ id: 'error_code' })}</Form.Label>
            <Form.Control
              type="text"
              {...register('ErrorCode')}
              isInvalid={!!errors.ErrorCode}
              className="dpp-form-control"
            >
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {errors.ErrorCode?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <Form.Group className="mb-4" controlId="LongText">
            <Form.Label>
              {intl.formatMessage({ id: 'description' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              {...register('LongText')}
              isInvalid={!!errors.LongText}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.LongText?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="StartOfFault">
            <Form.Label>{intl.formatMessage({ id: 'start_of_fault' })}</Form.Label>
            <Form.Control
              type="date"
              {...register('StartOfFault')}
              isInvalid={!!errors.StartOfFault}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.StartOfFault?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="AttachedMedia">
            <Form.Label>{intl.formatMessage({ id: 'attach_image' })}</Form.Label>
            <Form.Control
              type="file"
              custom
              multiple
              accept="image/jpeg,image/png,image/gif"
              {...register('AttachedMedia')}
              isInvalid={!!errors.AttachedMedia}
              className="dpp-form-control dpp-custom-file-label"
            />
            <Form.Control.Feedback type="invalid">
              {errors.AttachedMedia?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {/* Contact Information */}
      <h5 className="mt-4">{intl.formatMessage({ id: 'contact_information' })}</h5>
      <Row className="mt-4">
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="CustomerNumber">
            <Form.Label>{intl.formatMessage({ id: 'customer_number' })}</Form.Label>
            <Form.Control
              type="text"
              {...register('CustomerNumber')}
              isInvalid={!!errors.CustomerNumber}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.CustomerNumber?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="Company">
            <Form.Label>{intl.formatMessage({ id: 'company' })}</Form.Label>
            <Form.Control
              type="text"
              {...register('Company')}
              isInvalid={!!errors.Company}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.Company?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col lg={3}>
          <Form.Group className="mb-4" controlId="FirstName">
            <Form.Label>
              {intl.formatMessage({ id: 'first_name' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register('FirstName')}
              isInvalid={!!errors.FirstName}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.FirstName?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={3}>
          <Form.Group className="mb-4" controlId="LastName">
            <Form.Label>
              {intl.formatMessage({ id: 'last_name' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register('LastName')}
              isInvalid={!!errors.LastName}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.LastName?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="Email">
            <Form.Label>
              {intl.formatMessage({ id: 'email' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              {...register('Email')}
              isInvalid={!!errors.Email}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.Email?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        </Row>
        <Row>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="Phone">
            <Form.Label>{intl.formatMessage({ id: 'phone' })}</Form.Label>
            <Form.Control
              type="text"
              {...register('Phone')}
              isInvalid={!!errors.Phone}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.Phone?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="Street">
            <Form.Label>{intl.formatMessage({ id: 'street' })}</Form.Label>
            <Form.Control
              type="text"
              {...register('Street')}
              isInvalid={!!errors.Street}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.Street?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="CityTown">
            <Form.Label>{intl.formatMessage({ id: 'city_town' })}</Form.Label>
            <Form.Control
              type="text"
              {...register('CityTown')}
              isInvalid={!!errors.CityTown}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.CityTown?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="Zipcode">
            <Form.Label>{intl.formatMessage({ id: 'zip_code' })}</Form.Label>
            <Form.Control
              type="text"
              {...register('Zipcode')}
              isInvalid={!!errors.Zipcode}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.Zipcode?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="StateCounty">
            <Form.Label>{intl.formatMessage({ id: 'state_county' })}</Form.Label>
            <Form.Control
              type="text"
              {...register('StateCounty')}
              isInvalid={!!errors.StateCounty}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.StateCounty?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="NationalCode">
            <Form.Label>{intl.formatMessage({ id: 'country' })}</Form.Label>
            <Form.Control
              type="text"
              {...register('NationalCode')}
              isInvalid={!!errors.NationalCode}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.NationalCode?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {/* Save / Cancel Buttons */}
      {showButtons && (
        <div className="d-flex justify-content-end gap-2">
          <Button
            variant="outline-secondary"
            onClick={handleCancel}
            className="dpp-modal-button"
          >
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
          {isDemo ? (
            <OverlayTrigger
              placement="auto"
              overlay={<Tooltip id="save-tooltip">{noAccessMessage}</Tooltip>}
            >
              <span className="d-inline-block">
                <Button
                  variant="primary"
                  type="submit"
                  disabled
                  className="dpp-modal-button"
                  style={{ pointerEvents: 'none' }}
                >
                  {intl.formatMessage({ id: 'save' })}
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <Button
              variant="primary"
              type="submit"
              disabled={!isDirty || !isValid}
              className="dpp-modal-button"
            >
              {intl.formatMessage({ id: 'save' })}
            </Button>
          )}
        </div>
      )}
    </Form>
  );
};

export default ReportProblemForm;